import React from "react";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";
import { Stethoscope, ShieldCheck, AlertTriangle } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14 flex-1" data-testid="about-page">
        <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">About</div>
        <h1 className="font-heading text-4xl font-extrabold text-[#111827] mt-2">About RapidCare SA</h1>

        <div className="mt-8 p-5 rounded-lg bg-[#EFF4FF] border border-[#0B5FFF]/20 flex items-start gap-3">
          <Stethoscope className="w-5 h-5 text-[#111827] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#111827] font-medium">
            Built and maintained by a single South African doctor. RapidCare SA is a clinical reference tool curated against published South African guidelines and placed under ongoing review by healthcare professionals — to support colleagues at the point of care.
          </div>
        </div>

        <div className="prose prose-slate max-w-none mt-8 text-slate-700 leading-relaxed space-y-6">
          <p>
            Developed independently, the platform prioritises safe, practical, point-of-care reference content while maintaining clinician oversight in all decision-making. It provides evidence-based protocols, medication references, paediatric dosing, antibiotic guidance, clinical calculators, and an ICD-10 code-lookup — all aligned with South African treatment guidelines.
          </p>

          <p>
            Content is curated against the public South African guidelines cited on each item (NDoH Standard Treatment Guidelines and Essential Medicines List, SAMF, SEMDSA, EMSSA, SA Heart, SA Thoracic, CCSSA, IMCI, and peers). Each entry is structured for point-of-care usability and safety flags, and is placed under <strong>ongoing review by healthcare professionals</strong> so that corrections, updates, and guideline changes are incorporated on a rolling basis.
          </p>

          <section className="mt-8 p-5 rounded-lg bg-amber-50 border border-amber-200" data-testid="sahpra-section">
            <h2 className="font-heading font-bold text-amber-900 flex items-center gap-2 text-lg">
              <AlertTriangle className="w-4 h-4" /> SAHPRA & regulatory status
            </h2>
            <p className="mt-3 text-sm text-amber-900">
              <strong>RapidCare SA is NOT a medical device.</strong> It is a clinical reference and educational tool. RapidCare SA is <strong>not registered with the South African Health Products Regulatory Authority (SAHPRA)</strong> as a medical device, in vitro diagnostic, or therapeutic product — because it does not perform any of the regulated functions of a medical device under the Medicines and Related Substances Act and SAHPRA's General Regulations.
            </p>
            <p className="mt-3 text-sm text-amber-900">
              Specifically, the platform <strong>does not</strong>:
            </p>
            <ul className="mt-2 ml-5 list-disc text-sm text-amber-900 space-y-1">
              <li>Make autonomous clinical diagnoses, prescribing decisions or treatment recommendations for individual patients;</li>
              <li>Perform real-time patient monitoring or analyse patient-derived physiological signals;</li>
              <li>Interpret diagnostic images, laboratory results or other patient-specific data on the clinician's behalf;</li>
              <li>Control or modify the function of any physical medical equipment.</li>
            </ul>
            <p className="mt-3 text-sm text-amber-900">
              All output from RapidCare SA — protocols, drug references, calculator scores, ICD-10 codes — is generic reference content. <strong>It must be reviewed, interpreted, and approved by a qualified medical practitioner before being applied to any patient.</strong> Final clinical responsibility always remains with the registered healthcare professional. RapidCare SA operates in accordance with SAHPRA guidance on non-device clinical reference software and aligns with the relevant principles of the POPI Act and Health Professions Act.
            </p>
          </section>

          <section className="mt-8 p-5 rounded-lg bg-slate-50 border border-slate-200">
            <h2 className="font-heading font-bold text-[#111827] flex items-center gap-2 text-lg">
              <ShieldCheck className="w-4 h-4 text-[#111827]" /> Reference only — clinician-approved
            </h2>
            <p className="mt-3 text-sm">
              <strong>This tool is for reference only and requires a qualified medical practitioner to approve any treatments.</strong> Every item — including doses, calculators, codes, and protocol steps — must be reviewed, interpreted, and approved by a qualified medical practitioner before being applied to a patient.
            </p>
            <p className="mt-3 text-sm">
              Patient privacy is paramount. Users must never enter patient-identifiable information into the platform.
            </p>
          </section>

          <p>
            Your subscription helps keep this platform running, updated, and available to healthcare professionals who rely on it daily.
          </p>

          <p className="text-sm text-slate-600">
            Questions, corrections, or feedback? Email <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] underline">rapidcaresasupport@gmail.com</a>.
          </p>

          <p className="text-xs text-slate-500 border-t border-slate-200 pt-4">
            © {new Date().getFullYear()} RapidCare SA. All rights reserved. The RapidCare SA name, platform, user interface, clinical content curation, and compiled database are the intellectual property of the developer and may not be copied, republished, resold, or redistributed without written permission. See our <a href="/terms" className="text-[#111827] underline">Terms &amp; Conditions</a>.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
